/**
 * 品牌设置：商标图片自助上传
 * - 存储：localStorage `changfa_brand_logo_v1`（192×192 PNG dataURL，本设备生效）
 * - 消费：PC 侧边栏 / 移动抽屉 / 启动屏 的 Logo，以及浏览器标签 favicon
 */

const BRAND_LOGO_KEY = 'changfa_brand_logo_v1';
/** 上传图统一缩放到的边长（favicon / 高清屏都够用，且 localStorage 体积小） */
const BRAND_LOGO_SIZE = 192;
/** dataURL 体积上限（约 300KB），超出说明图片异常复杂，拒绝保存防 quota 崩溃 */
const MAX_DATAURL_LENGTH = 320_000;

export function getBrandLogo(): string | null {
  try {
    const v = localStorage.getItem(BRAND_LOGO_KEY);
    return v && v.startsWith('data:image/') ? v : null;
  } catch {
    return null;
  }
}

export function setBrandLogo(dataUrl: string): boolean {
  if (!dataUrl.startsWith('data:image/') || dataUrl.length > MAX_DATAURL_LENGTH) return false;
  try {
    localStorage.setItem(BRAND_LOGO_KEY, dataUrl);
    return true;
  } catch {
    return false;
  }
}

export function clearBrandLogo() {
  try {
    localStorage.removeItem(BRAND_LOGO_KEY);
  } catch { /* Best-effort operation; failure is non-fatal. */ }
}

/**
 * 把用户选的图片文件缩放为 BRAND_LOGO_SIZE 的正方形 PNG dataURL（居中裁剪）。
 * 返回 null 表示文件无法解析。
 */
export async function fileToBrandDataUrl(file: File): Promise<string | null> {
  if (!file.type.startsWith('image/')) return null;
  const bitmap = await loadBitmap(file);
  if (!bitmap) return null;

  const canvas = document.createElement('canvas');
  canvas.width = BRAND_LOGO_SIZE;
  canvas.height = BRAND_LOGO_SIZE;
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;

  // 居中裁剪为正方形后等比缩放
  const side = Math.min(bitmap.width, bitmap.height);
  const sx = (bitmap.width - side) / 2;
  const sy = (bitmap.height - side) / 2;
  ctx.drawImage(bitmap, sx, sy, side, side, 0, 0, BRAND_LOGO_SIZE, BRAND_LOGO_SIZE);
  if ('close' in bitmap && typeof bitmap.close === 'function') bitmap.close();
  return canvas.toDataURL('image/png');
}

async function loadBitmap(file: File): Promise<ImageBitmap | HTMLImageElement | null> {
  // 优先 createImageBitmap（快、支持 webp/svg），失败回退 <img>
  try {
    if ('createImageBitmap' in window) return await createImageBitmap(file);
  } catch { /* Best-effort operation; failure is non-fatal. */ }
  return new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => resolve(null);
    img.src = url;
  });
}

/**
 * 运行时替换浏览器标签图标（favicon）。
 * 有商标 → 动态 <link rel="icon">；无商标 → 移除动态 link，回落浏览器默认。
 * 在启动时与上传/清除后各调用一次。
 */
export function applyBrandFavicon() {
  const LINK_ID = 'changfa-brand-favicon';
  const prev = document.getElementById(LINK_ID) as HTMLLinkElement | null;
  const logo = getBrandLogo();
  if (!logo) {
    prev?.remove();
    return;
  }
  const link = prev ?? document.createElement('link');
  link.id = LINK_ID;
  link.rel = 'icon';
  link.type = 'image/png';
  link.href = logo;
  if (!prev) document.head.appendChild(link);
}
